import { FEATURES } from './features';

export enum PLAN_OPTIONS {
  LIVE_SALES_ACADEMY = 'LIVE_SALES_ACADEMY',
  SALES_MENTORSHIP = 'SALES_MENTORSHIP',
  BRANDING = 'BRANDING',
  STUDIO_TEMPLATES = 'STUDIO_TEMPLATES',
  AMAZON_PRODUCT_CAROUSEL = 'AMAZON_PRODUCT_CAROUSEL',
  MEDIA_UPLOADS = 'MEDIA_UPLOADS',
  MULTI_CAMERA = 'MULTI_CAMERA',
  HOST_AND_GUEST_SCREEN_SHARE = 'HOST_AND_GUEST_SCREEN_SHARE',
  STREAM_PARTICIPANTS = 'STREAM_PARTICIPANTS',
  VIRTUAL_BACKGROUND = 'VIRTUAL_BACKGROUND',
  SCREEN_LAYOUT = 'SCREEN_LAYOUT',
  AMAZON_CHANNEL = 'AMAZON_CHANNEL',
  MULTI_STREAMING = 'MULTI_STREAMING',
  CUSTOM_RTMP = 'CUSTOM_RTMP',
  OFFLINE_RECORDING = 'OFFLINE_RECORDING',
  FULL_HD = 'FULL_HD',
  LIVE_STATS = 'LIVE_STATS',
  DOWNLOAD_CONTENT = 'DOWNLOAD_CONTENT',
  TRIMMING_VIDEO = 'TRIMMING_VIDEO',
  REPURPOSING = 'REPURPOSING',
  POST_SHOW_ANALYTICS = 'POST_SHOW_ANALYTICS',
  LOWER_THIRD = 'LOWER_THIRD',
  COMMENTS_ASSISTANT = 'COMMENTS_ASSISTANT',
  SCRIPT_GENERATOR = 'SCRIPT_GENERATOR',
  SUPPORT = 'SUPPORT',
  STREAM_QUOTA = 'STREAM_QUOTA',
  BROADCAST_STORAGE = 'BROADCAST_STORAGE',
  COMMENTS_FROM_AMAZON = 'COMMENTS_FROM_AMAZON',
  AMAZON_AFFILIATE_LINKS = 'AMAZON_AFFILIATE_LINKS',
  TEAM_MEMBERS = 'TEAM_MEMBERS',
  WIDGETS = 'WIDGETS',
}

export enum SUBSCRIPTION_PERIOD {
  EMPTY = '',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  LIFETIME = 'lifetime',
}

export const PLAN_OPTIONS_NAMES: Record<
  PLAN_OPTIONS,
  { name: string; description: string; comingSoon?: boolean }
> = {
  BROADCAST_STORAGE: {
    name: 'Broadcast storage',
    description: 'Download or edit your content during this time.',
  },
  COMMENTS_ASSISTANT: {
    name: 'Comments Assistant',
    description:
      'Automatically showcase positive comments and questions on screen for effective engagement with your audience.',
  },
  LOWER_THIRD: {
    name: 'Lower third',
    description: 'Show viewer’s comments on screen or add any running lines/lower thirds.',
  },
  SCRIPT_GENERATOR: {
    name: 'Script Generator',
    description: 'Generates scripts for your broadcast based on the product description.',
    comingSoon: true,
  },
  STREAM_QUOTA: {
    name: 'Stream quota',
    description: 'Number of available broadcasts per month.',
  },
  SUPPORT: {
    description: 'Contact our support via chat, email or Facebook Messenger.',
    name: 'Support',
  },
  DOWNLOAD_CONTENT: {
    name: 'Download content',
    description: 'Download your recent broadcasts.',
  },
  POST_SHOW_ANALYTICS: {
    name: 'Post-show analytics',
    description: 'Analyse your show and compare historical data for constant improvement.',
    comingSoon: true,
  },
  REPURPOSING: {
    name: 'Content repurposing',
    description: 'Promote your shows by repurposing content on social media platforms.',
  },
  TRIMMING_VIDEO: {
    name: 'Trimming video',
    description: 'Trim your favourite parts for further content repurposing.',
  },
  AMAZON_CHANNEL: {
    name: 'Amazon channel',
    description: 'Stream to one or more Amazon channels at the same time.',
  },
  FULL_HD: {
    name: 'Full HD (1080p) quality',
    description: 'Stream in 1080p to platforms that support this resolution.',
  },
  LIVE_STATS: {
    name: 'Live stats',
    description: 'Check the number of views, followers and reactions while live.',
  },
  MULTI_STREAMING: {
    name: 'Multi-streaming on Facebook, Youtube, LinkedIn, Instagram, TikTok',
    description:
      'Stream simultaneously across major platforms to maximize your reach.',
  },
  CUSTOM_RTMP: {
    name: 'Custom RTMP',
    description: 'Stream to your own website or other unique destinations beyond standard platforms.',
  },
  OFFLINE_RECORDING: {
    name: 'Offline recording',
    description: 'Create content without going live.',
  },
  HOST_AND_GUEST_SCREEN_SHARE: {
    name: 'Host and guest screen share',
    description: 'Share a screen during your live stream.',
  },
  MEDIA_UPLOADS: {
    name: 'Media File Uploads',
    description:
      'Upload images, slides and videos from PC.',
  },
  MULTI_CAMERA: {
    name: 'Multi-camera',
    description:
      'Connect and switch between multiple cameras during your live stream.',
  },
  SCREEN_LAYOUT: {
    name: 'Screen layouts',
    description: 'Different options for arranging the feeds on screen.',
  },
  STREAM_PARTICIPANTS: {
    name: 'Stream participants',
    description: 'Max number of onscreen participants.',
  },
  VIRTUAL_BACKGROUND: {
    name: 'Virtual background',
    description: 'Blur your background or replace it with an image.',
  },
  LIVE_SALES_ACADEMY: {
    name: 'Live Sales Academy',
    description:
      'Supercharge your Amazon Live sales by enrolling in a dedicated series of classes featuring Be.Live’s top sellers and professional live streamers.',
  },
  SALES_MENTORSHIP: {
    name: 'Sales mentorship',
    description:
      'Join our supportive community with Be.Live’s top sellers and get access to private mentoring sessions.',
  },
  BRANDING: {
    name: 'Custom Branding',
    description: 'Add your custom logo, overlay, background, and business colors.',
  },
  STUDIO_TEMPLATES: {
    name: 'Studio templates',
    description: 'Get prepared for multiple shows beforehand.',
  },
  AMAZON_PRODUCT_CAROUSEL: {
    name: 'Amazon product carousel',
    description:
      'Import products from the Amazon application to the studio and show them in-stream.',
  },
  COMMENTS_FROM_AMAZON: {
    name: 'Comments from Amazon',
    description: 'Receive comments from Amazon destination and show them on screen!',
  },
  AMAZON_AFFILIATE_LINKS: {
    name: 'Sharing Amazon affiliate links',
    description:
      'Share your Amazon affiliate links in comments while multi-streaming to Facebook and Youtube!',
  },
  TEAM_MEMBERS: {
    name: 'Team members',
    description:
      'Enables multiple users to control the stream and access content post-stream.',
  },
  WIDGETS: {
    name: 'Engagement Widgets',
    description: 'Utilize dynamic tools to increase viewer engagement and interaction.',
  }
};

export const PLAN_OPTIONS_GROUPS: {
  name: string;
  options: PLAN_OPTIONS[];
  comingSoon?: boolean;
}[] = [
  {
    name: 'Pre-broadcast',
    options: [PLAN_OPTIONS.BRANDING, PLAN_OPTIONS.STUDIO_TEMPLATES, PLAN_OPTIONS.TEAM_MEMBERS],
  },
  {
    name: 'Feeds',
    options: [
      PLAN_OPTIONS.STREAM_PARTICIPANTS,
      PLAN_OPTIONS.HOST_AND_GUEST_SCREEN_SHARE,
      PLAN_OPTIONS.MEDIA_UPLOADS,
      PLAN_OPTIONS.MULTI_CAMERA,
      PLAN_OPTIONS.FULL_HD,
    ],
  },
  {
    name: 'Destinations',
    options: [PLAN_OPTIONS.MULTI_STREAMING, PLAN_OPTIONS.CUSTOM_RTMP, PLAN_OPTIONS.OFFLINE_RECORDING],
  },
  {
    name: 'Amazon Live',
    options: [
      PLAN_OPTIONS.AMAZON_CHANNEL,
      PLAN_OPTIONS.COMMENTS_FROM_AMAZON,
      PLAN_OPTIONS.AMAZON_PRODUCT_CAROUSEL,
      PLAN_OPTIONS.AMAZON_AFFILIATE_LINKS,
    ],
  },
  {
    name: 'Engagement tools & AI',
    options: [PLAN_OPTIONS.LOWER_THIRD, PLAN_OPTIONS.WIDGETS, PLAN_OPTIONS.COMMENTS_ASSISTANT, PLAN_OPTIONS.SCRIPT_GENERATOR],
  },
  {
    name: 'Video Editor',
    options: [PLAN_OPTIONS.TRIMMING_VIDEO, PLAN_OPTIONS.REPURPOSING],
  },
  {
    name: 'Support',
    options: [PLAN_OPTIONS.SUPPORT],
  },
  {
    name: 'Other',
    options: [PLAN_OPTIONS.STREAM_QUOTA, PLAN_OPTIONS.BROADCAST_STORAGE],
  },
];

export enum LegacyPlansIds {
  STANDARD_PLUS = 4,
  APPSUMO_TIER1 = 6,
  APPSUMO_TIER2 = 7,
  APPSUMO_TIER3 = 8,
  KALTURA = 9,
}

export enum ActualPlansIds {
  FREE = 1,
  PRO = 5,
  ZERO = 10,
  STARTER = 11,
  GROWTH = 12,
  AMAZON = undefined,
}

export const PlansIds = { ...LegacyPlansIds, ...ActualPlansIds };
export type PlansIds = LegacyPlansIds | ActualPlansIds;
export const upgradePlansOrder: PlansIds[] = [
  ActualPlansIds.FREE,
  ActualPlansIds.ZERO,
  LegacyPlansIds.APPSUMO_TIER1,
  LegacyPlansIds.APPSUMO_TIER2,
  LegacyPlansIds.APPSUMO_TIER3,
  ActualPlansIds.STARTER,
  LegacyPlansIds.STANDARD_PLUS,
  ActualPlansIds.PRO,
  ActualPlansIds.GROWTH,
];

export interface Product {
  name: string;
  planId?: ActualPlansIds;
  buttonText: string;
  buttonLink?: string;
  description: string
  special?: string;
  isExpanded?: boolean;
  withoutDiscounts?: boolean;
  mobileOnly?: boolean;
  optionsList: { available?: (string | string[] | { value: string; comingSoon?: boolean })[]; notAvailable?: string[] };
  options?: Partial<Record<PLAN_OPTIONS, { text?: string | number; description?: string }>>;
  paymentPlanPaths: Partial<Record<SUBSCRIPTION_PERIOD, string>>;
  features: FEATURES[];
}

export const PRODUCTS: Product[] = [
  {
    name: 'Free',
    planId: PlansIds.FREE,
    buttonText: 'Upgrade Now',
    description: 'Includes these features:',
    isExpanded: true,
    optionsList: {
      available: [
        '3 streams per month',
        '2 on-screen participants',
        'Custom Branding + Watermark',
        'Lower third',
        '720p',
      ],
    },
    options: {
      [PLAN_OPTIONS.BRANDING]: {},
      [PLAN_OPTIONS.STUDIO_TEMPLATES]: { text: 5 },

      [PLAN_OPTIONS.HOST_AND_GUEST_SCREEN_SHARE]: {},
      [PLAN_OPTIONS.STREAM_PARTICIPANTS]: { text: 2 },
      [PLAN_OPTIONS.SCREEN_LAYOUT]: { text: 19 },
      [PLAN_OPTIONS.TEAM_MEMBERS]: { text: 1 },

      [PLAN_OPTIONS.AMAZON_CHANNEL]: { text: 1 },
      [PLAN_OPTIONS.MULTI_STREAMING]: {},
      [PLAN_OPTIONS.CUSTOM_RTMP]: { text: 1 },
      [PLAN_OPTIONS.OFFLINE_RECORDING]: {},

      [PLAN_OPTIONS.COMMENTS_FROM_AMAZON]: {},

      [PLAN_OPTIONS.DOWNLOAD_CONTENT]: {},
      [PLAN_OPTIONS.TRIMMING_VIDEO]: {},

      [PLAN_OPTIONS.SUPPORT]: { text: 'Low priority support' },

      [PLAN_OPTIONS.LOWER_THIRD]: {},

      [PLAN_OPTIONS.WIDGETS]: {},

      [PLAN_OPTIONS.STREAM_QUOTA]: { text: '3 shows' },
      [PLAN_OPTIONS.BROADCAST_STORAGE]: { text: '30 days' },
    },
    paymentPlanPaths: {
    },
    features: [],
  },
  {
    name: 'Starter',
    planId: PlansIds.STARTER,
    buttonText: 'Upgrade Now',
    description: 'Everything in Free and:',
    isExpanded: true,
    optionsList: {
      available: [
        '7 streams per month',
        '3x Multi-streaming',
        '4 on-screen participants',
        '30 days recording storage',
        'Custom Branding',
        'Team: 2 members',
        '720p',
      ],
    },
    options: {
      [PLAN_OPTIONS.BRANDING]: {},
      [PLAN_OPTIONS.STUDIO_TEMPLATES]: { text: 5 },

      [PLAN_OPTIONS.MEDIA_UPLOADS]: {},
      [PLAN_OPTIONS.HOST_AND_GUEST_SCREEN_SHARE]: {},
      [PLAN_OPTIONS.STREAM_PARTICIPANTS]: { text: 4 },
      [PLAN_OPTIONS.SCREEN_LAYOUT]: { text: 19 },
      [PLAN_OPTIONS.TEAM_MEMBERS]: { text: 2 },

      [PLAN_OPTIONS.AMAZON_CHANNEL]: { text: 1 },
      [PLAN_OPTIONS.MULTI_STREAMING]: {},
      [PLAN_OPTIONS.CUSTOM_RTMP]: { text: 1 },
      [PLAN_OPTIONS.OFFLINE_RECORDING]: {},

      [PLAN_OPTIONS.COMMENTS_FROM_AMAZON]: {},

      [PLAN_OPTIONS.DOWNLOAD_CONTENT]: {},
      [PLAN_OPTIONS.TRIMMING_VIDEO]: {},

      [PLAN_OPTIONS.SUPPORT]: { text: 'Human support' },

      [PLAN_OPTIONS.LOWER_THIRD]: {},

      [PLAN_OPTIONS.WIDGETS]: {},

      [PLAN_OPTIONS.STREAM_QUOTA]: { text: '7 shows' },
      [PLAN_OPTIONS.BROADCAST_STORAGE]: { text: '30 days' },
    },
    paymentPlanPaths: {
      [SUBSCRIPTION_PERIOD.MONTHLY]: 'starter',
      [SUBSCRIPTION_PERIOD.YEARLY]: 'starterannual',
    },
    features: [],
  },
  {
    name: 'Pro',
    planId: PlansIds.PRO,
    buttonText: 'Upgrade Now',
    description: 'Everything in Starter and:',
    isExpanded: true,
    optionsList: {
      available: [
        'Unlimited streaming',
        '5x Multi-streaming',
        '8 on-screen participants',
        'AI Comment Assistant',
        '60 days recording storage',
        'Custom Branding',
        'Team: 4 members',
        {
          value: 'Custom widget',
          comingSoon: true,
        },
        '1080p',
      ]
    },
    options: {
      [PLAN_OPTIONS.LIVE_SALES_ACADEMY]: {
        description: 'Only available for the annual subscription',
      },
      [PLAN_OPTIONS.SALES_MENTORSHIP]: {
        description: 'Only available for the annual subscription',
      },

      [PLAN_OPTIONS.BRANDING]: {},
      [PLAN_OPTIONS.STUDIO_TEMPLATES]: { text: 10 },

      [PLAN_OPTIONS.STREAM_PARTICIPANTS]: { text: 8 },
      [PLAN_OPTIONS.MEDIA_UPLOADS]: {},
      [PLAN_OPTIONS.MULTI_CAMERA]: {},
      [PLAN_OPTIONS.HOST_AND_GUEST_SCREEN_SHARE]: {},
      [PLAN_OPTIONS.TEAM_MEMBERS]: { text: 4 },

      [PLAN_OPTIONS.MULTI_STREAMING]: {},
      [PLAN_OPTIONS.CUSTOM_RTMP]: { text: 5 },
      [PLAN_OPTIONS.OFFLINE_RECORDING]: {},
      [PLAN_OPTIONS.FULL_HD]: {},

      [PLAN_OPTIONS.AMAZON_CHANNEL]: { text: '1' },
      [PLAN_OPTIONS.COMMENTS_FROM_AMAZON]: {},

      [PLAN_OPTIONS.LOWER_THIRD]: {},

      [PLAN_OPTIONS.COMMENTS_ASSISTANT]: {},

      [PLAN_OPTIONS.WIDGETS]: {},

      [PLAN_OPTIONS.TRIMMING_VIDEO]: {},

      [PLAN_OPTIONS.SUPPORT]: { text: 'Human support' },

      [PLAN_OPTIONS.STREAM_QUOTA]: { text: 'Unlimited' },
      [PLAN_OPTIONS.BROADCAST_STORAGE]: { text: '60 days' },
    },
    paymentPlanPaths: {
      [SUBSCRIPTION_PERIOD.MONTHLY]: 'pro',
      [SUBSCRIPTION_PERIOD.YEARLY]: 'proannual',
    },
    features: [],
  },
  {
    name: 'Growth',
    planId: PlansIds.GROWTH,
    buttonText: 'Upgrade Now',
    description: 'Everything in Pro and:',
    special: 'Power Users’ Choice',
    isExpanded: true,
    optionsList: {
      available: [
        'Unlimited streaming',
        '8x Multi-streaming',
        '10 on-screen participants',
        'AI Comment Assistant',
        '90 days recording storage',
        'Custom Branding',
        'Team: 6 members',
        {
          value: 'Custom widget',
          comingSoon: true,
        },
        '1080p',
      ],
    },
    options: {
      [PLAN_OPTIONS.LIVE_SALES_ACADEMY]: {
        description: 'Only available for the annual subscription',
      },
      [PLAN_OPTIONS.SALES_MENTORSHIP]: {
        description: 'Only available for the annual subscription',
      },

      [PLAN_OPTIONS.BRANDING]: {},
      [PLAN_OPTIONS.STUDIO_TEMPLATES]: { text: 10 },

      [PLAN_OPTIONS.AMAZON_PRODUCT_CAROUSEL]: {},
      [PLAN_OPTIONS.MEDIA_UPLOADS]: {},
      [PLAN_OPTIONS.MULTI_CAMERA]: {},
      [PLAN_OPTIONS.HOST_AND_GUEST_SCREEN_SHARE]: {},
      [PLAN_OPTIONS.STREAM_PARTICIPANTS]: { text: 10 },
      [PLAN_OPTIONS.VIRTUAL_BACKGROUND]: {},
      [PLAN_OPTIONS.SCREEN_LAYOUT]: { text: 19 },
      [PLAN_OPTIONS.MULTI_STREAMING]: {},
      [PLAN_OPTIONS.CUSTOM_RTMP]: { text: 8 },
      [PLAN_OPTIONS.OFFLINE_RECORDING]: {},
      [PLAN_OPTIONS.FULL_HD]: {},
      [PLAN_OPTIONS.TEAM_MEMBERS]: { text: 6 },

      [PLAN_OPTIONS.AMAZON_CHANNEL]: { text: 'Up to 5' },
      [PLAN_OPTIONS.COMMENTS_FROM_AMAZON]: {},
      [PLAN_OPTIONS.AMAZON_AFFILIATE_LINKS]: {},

      [PLAN_OPTIONS.DOWNLOAD_CONTENT]: {},
      [PLAN_OPTIONS.TRIMMING_VIDEO]: {},

      [PLAN_OPTIONS.REPURPOSING]: {},
      [PLAN_OPTIONS.POST_SHOW_ANALYTICS]: {},

      [PLAN_OPTIONS.LOWER_THIRD]: {},

      [PLAN_OPTIONS.COMMENTS_ASSISTANT]: {},
      [PLAN_OPTIONS.SCRIPT_GENERATOR]: {},

      [PLAN_OPTIONS.WIDGETS]: {},

      [PLAN_OPTIONS.SUPPORT]: { text: 'Human support' },

      [PLAN_OPTIONS.STREAM_QUOTA]: { text: 'Unlimited' },
      [PLAN_OPTIONS.BROADCAST_STORAGE]: { text: '90 days' },
    },
    paymentPlanPaths: {
      [SUBSCRIPTION_PERIOD.MONTHLY]: 'growth',
      [SUBSCRIPTION_PERIOD.YEARLY]: 'growthannual',
    },
    features: [],
  },
  // {
  //   name: 'Live Sales Academy',
  //   buttonText: 'Buy Now',
  //   buttonLink: 'https://belive.onfastspring.com/live-sales-academy',
  //   description: 'One-time payment',
  //   planId: PlansIds.AMAZON,
  //   isExpanded: true,
  //   withoutDiscounts: true,
  //   mobileOnly: true,
  //   optionsList: {
  //     available: [
  //       '8 weeks course on earning as an Amazon Influencer:',
  //       [
  //         'Mentorship with weekly live sessions',
  //         'Pre-recorded classes,',
  //         'Flexible learning schedule',
  //       ],
  //       'Monthly Round Tables with experienced Amazon Influencers',
  //       'Private Live Sellers Community',
  //     ],
  //   },
  //   paymentPlanPaths: {
  //     [SUBSCRIPTION_PERIOD.LIFETIME]: 'live-sales-academy',
  //   },
  //   features: [],
  // },
];
